import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bullseye,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  EmptyStateBody,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { TableIcon } from '@patternfly/react-icons';
import { useReportsData } from '../../hooks/useReportsData';
import { useReportFilterOptions } from '../../hooks/useReportFilterOptions';
import { useReportFiltersUrl } from '../../hooks/useReportFiltersUrl';
import { useNotifications } from '../../context/NotificationContext';
import { ExpandedRowsState, MatrixRow as MatrixRowType, ReportGroupingConfig, ReportViewType } from '../../types/reports';
import {
  calculateColumnCount,
  flattenVisibleRows,
  getAggregatedColumns,
  getCellWidthForView,
} from '../../utils/reportHelpers';
import { exportReportToExcel } from '../../utils/exportToExcel';
import { MatrixHeader } from './MatrixHeader';
import { GrandTotalRow, MatrixRow } from './MatrixRow';
import { ReportsToolbar } from './ReportsToolbar';
import './ReportsMatrix.css';

const collectLabels = (rows: MatrixRowType[]): string[] => {
  const labels = new Set<string>();
  const walk = (r: MatrixRowType[]) => {
    for (const row of r) {
      row.labels?.forEach((l) => labels.add(l));
      if (row.children) walk(row.children);
    }
  };
  walk(rows);
  return Array.from(labels).sort();
};

const MIN_LABEL_WIDTH = 120;
const MAX_LABEL_WIDTH = 600;
const DEFAULT_LABEL_WIDTH = 220;

export const ReportsMatrix: React.FC = () => {
  const { initialFilters, updateUrl } = useReportFiltersUrl();
  const { notifyError } = useNotifications();

  // Filter options - loaded ONCE on mount
  const {
    availableUsers,
    availableProjects,
    isAdmin,
    loading: filtersLoading,
  } = useReportFilterOptions();

  // Report data - loaded when filters change
  const {
    reportData,
    loading: dataLoading,
    error,
    filters,
    updateFilters,
    refresh,
  } = useReportsData({ filters: initialFilters });

  // Combined loading state
  const loading = filtersLoading || dataLoading;

  // Show error as notification instead of replacing the view
  // Using error.id as dependency ensures notification shows for each new error
  useEffect(() => {
    if (error) {
      notifyError('Error loading report data', error.message);
    }
  }, [error?.id, notifyError]);

  // Sync filters to URL
  useEffect(() => {
    updateUrl(filters);
  }, [filters, updateUrl]);

  const [expandedRows, setExpandedRows] = useState<ExpandedRowsState>({});
  const [labelColumnWidth, setLabelColumnWidth] = useState(DEFAULT_LABEL_WIDTH);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggleRow = useCallback((rowId: string, currentExpanded: boolean) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !currentExpanded,
    }));
  }, []);

  const flattenedRows = useMemo(() => {
    if (!reportData) return [];
    return flattenVisibleRows(reportData.rows, expandedRows);
  }, [reportData, expandedRows]);

  const labelsSetRef = useRef(new Set<string>());
  const [availableLabels, setAvailableLabels] = useState<string[]>([]);
  useEffect(() => {
    if (!reportData) return;
    const newLabels = collectLabels(reportData.rows);
    let changed = false;
    for (const l of newLabels) {
      if (!labelsSetRef.current.has(l)) {
        labelsSetRef.current.add(l);
        changed = true;
      }
    }
    if (changed) {
      setAvailableLabels(Array.from(labelsSetRef.current).sort());
    }
  }, [reportData]);

  // Calculate aggregated columns for weekly/monthly views
  const aggregatedColumns = useMemo(() => {
    if (!reportData) return null;
    return getAggregatedColumns(reportData.columnDates, filters.viewType);
  }, [reportData, filters.viewType]);

  const columnCount = useMemo(() => {
    if (!reportData) return 7;
    if (aggregatedColumns) {
      return aggregatedColumns.length;
    }
    return calculateColumnCount(reportData.dateRange);
  }, [reportData, aggregatedColumns]);

  const cellWidth = useMemo(() => getCellWidthForView(filters.viewType), [filters.viewType]);

  const handleDateRangeChange = useCallback(
    (dateRange: { startDate: string; endDate: string }) => {
      updateFilters({ dateRange });
      setExpandedRows({});
    },
    [updateFilters]
  );

  const handleGroupingChange = useCallback(
    (groupingConfig: ReportGroupingConfig) => {
      updateFilters({ groupingConfig });
      setExpandedRows({});
    },
    [updateFilters]
  );

  const handleUsersChange = useCallback(
    (selectedUsers: string[]) => {
      updateFilters({ selectedUsers });
    },
    [updateFilters]
  );

  const handleProjectsChange = useCallback(
    (selectedProjects: string[]) => {
      updateFilters({ selectedProjects });
    },
    [updateFilters]
  );

  const handleLabelsChange = useCallback(
    (selectedLabels: string[]) => {
      updateFilters({ selectedLabels });
    },
    [updateFilters]
  );

  const handleViewTypeChange = useCallback(
    (viewType: ReportViewType) => {
      updateFilters({ viewType });
    },
    [updateFilters]
  );

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeRef.current = { startX: e.clientX, startWidth: labelColumnWidth };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizeRef.current) return;
      const delta = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = Math.min(MAX_LABEL_WIDTH, Math.max(MIN_LABEL_WIDTH, resizeRef.current.startWidth + delta));
      setLabelColumnWidth(newWidth);
    };

    const handleMouseUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [labelColumnWidth]);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const handleExport = useCallback(() => {
    if (reportData) {
      exportReportToExcel(reportData, { viewType: filters.viewType });
    }
  }, [reportData, filters.viewType]);

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when in fullscreen
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  const cardContent = (
    <Card
      ref={containerRef}
      className={`reports-matrix-card${isFullscreen ? ' reports-matrix-card--fullscreen' : ''}`}
    >
      <CardHeader>
        <CardTitle>
          <TableIcon /> Time matrix
        </CardTitle>
      </CardHeader>
      <CardBody>
        <ReportsToolbar
          dateRange={filters.dateRange}
          groupingConfig={filters.groupingConfig}
          viewType={filters.viewType}
          availableUsers={availableUsers}
          availableProjects={availableProjects}
          selectedUsers={filters.selectedUsers || []}
          selectedProjects={filters.selectedProjects || []}
          availableLabels={availableLabels}
          selectedLabels={filters.selectedLabels || []}
          isAdmin={isAdmin}
          loading={loading}
          isFullscreen={isFullscreen}
          onDateRangeChange={handleDateRangeChange}
          onGroupingChange={handleGroupingChange}
          onViewTypeChange={handleViewTypeChange}
          onUsersChange={handleUsersChange}
          onProjectsChange={handleProjectsChange}
          onLabelsChange={handleLabelsChange}
          onRefresh={refresh}
          onToggleFullscreen={handleToggleFullscreen}
          onExport={handleExport}
        />

        {loading && !reportData ? (
          <Bullseye className="reports-matrix__loading">
            <Spinner size="xl" />
          </Bullseye>
        ) : !reportData || flattenedRows.length === 0 ? (
          <EmptyState>
            <Title headingLevel="h4" size="lg">
              No work logs found
            </Title>
            <EmptyStateBody>
              No work logs found for the selected date range and filters. Try adjusting your filters or date range.
            </EmptyStateBody>
          </EmptyState>
        ) : (
          <div
            className="matrix-container"
            style={{
              '--column-count': columnCount,
              '--label-column-width': `${labelColumnWidth}px`,
              '--cell-width': `${cellWidth}px`,
            } as React.CSSProperties}
          >
            <MatrixHeader
              columns={reportData.columnDates}
              aggregatedColumns={aggregatedColumns}
              viewType={filters.viewType}
              onResizeStart={handleResizeStart}
            />
            <div className="matrix-body">
              {flattenedRows.map((row) => {
                // Use uniqueKey for expanded state - allows same entity under different parents
                const isExpanded = expandedRows[row.uniqueKey] ?? row.isExpanded ?? true;
                return (
                  <MatrixRow
                    key={row.uniqueKey}
                    row={row}
                    isExpanded={isExpanded}
                    aggregatedColumns={aggregatedColumns}
                    onToggle={() => handleToggleRow(row.uniqueKey, isExpanded)}
                  />
                );
              })}
            </div>
            <GrandTotalRow
              cells={reportData.grandTotal.dailyTotals}
              aggregatedColumns={aggregatedColumns}
              totalSeconds={reportData.grandTotal.totalSeconds}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );

  // Use portal to render fullscreen outside of PatternFly Page structure
  if (isFullscreen) {
    return createPortal(cardContent, document.body);
  }

  return cardContent;
};
